import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 } from "uuid";
import ConnectionData from "../../../js/connection/ConnectionData";
import {
    CanvasPath,
    getConvexHull,
    isPointInsideConvexHull,
    Point
} from "../../../js/drawing/CanvasPath";
import CanvasDrawing from "../../../js/drawing/CanvasDrawing";
import { DrawingMode } from "./DrawingControls";

type Props = {
    drawingMode: DrawingMode;
    strokeColor: string;
    canvasDrawing: CanvasDrawing;
    connectionData: ConnectionData;
};

export default function DrawingOverlay({
    drawingMode,
    strokeColor,
    canvasDrawing,
    connectionData
}: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const contextRef = useRef<CanvasRenderingContext2D | null>(null);
    const [paths, setPaths] = useState<Record<string, CanvasPath>>({});

    // Local camera is CSS-mirrored for the owner; annotations must live in
    // unmirrored "true video" space so remotes see strokes on the correct side.
    const isMirroredSelf =
        !!connectionData.isLocal && !connectionData.isScreen;

    const lastTimestamp = useMemo(
        () =>
            Object.values(paths)
                .map((p) => p.timestamp)
                .sort((a, b) => a - b)
                .at(-1) || 0,
        [paths]
    );

    const [isDrawing, setIsDrawing] = useState(false);
    const [currentPoints, setCurrentPoints] = useState<Point[]>([]);

    useEffect(() => {
        contextRef.current = canvasRef.current!.getContext("2d");
    }, []);

    useEffect(() => {
        if (canvasDrawing.myConnectionData!.id === connectionData.id) {
            return;
        }

        const interval = window.setInterval(() => {
            canvasDrawing.sendSyncPing(connectionData.id, lastTimestamp);
        }, 1000);

        return () => window.clearInterval(interval);
    }, [connectionData.id, canvasDrawing, lastTimestamp]);

    const getCanvasResolutionAndOffset = useCallback(() => {
        const rect = canvasRef.current!.getBoundingClientRect();

        const videoAspect =
            connectionData.videoWidth / connectionData.videoHeight;
        const containerAspect = rect.width / rect.height;

        const videoEl = document.getElementById("video");
        const isCoverMode =
            videoEl?.classList.contains("layout-cover") ?? false;

        let displayWidth, displayHeight, offsetX, offsetY;

        if (isCoverMode) {
            // Cover mode: video extends beyond container (crop/zoom)
            if (containerAspect > videoAspect) {
                displayWidth = rect.width;
                displayHeight = rect.width / videoAspect;
                offsetX = 0;
                offsetY = (rect.height - displayHeight) / 2;
            } else {
                displayWidth = rect.height * videoAspect;
                displayHeight = rect.height;
                offsetX = (rect.width - displayWidth) / 2;
                offsetY = 0;
            }
        } else {
            // Contain mode: video fits within container (letterbox/pillarbox)
            if (containerAspect > videoAspect) {
                // container wider → pillarbox (black bars on sides)
                displayHeight = rect.height;
                displayWidth = rect.height * videoAspect;
                offsetX = (rect.width - displayWidth) / 2;
                offsetY = 0;
            } else {
                // container taller → letterbox (black bars top/bottom)
                displayWidth = rect.width;
                displayHeight = rect.width / videoAspect;
                offsetX = 0;
                offsetY = (rect.height - displayHeight) / 2;
            }
        }

        return { offsetX, offsetY, displayWidth, displayHeight };
    }, [connectionData.videoWidth, connectionData.videoHeight]);

    const videoPointToCanvas = useCallback(
        (point: Point) => {
            // Flip for mirrored self-view so stored true-video coords align with CSS mirror
            const nx = isMirroredSelf ? 1 - point.x : point.x;
            const videoX = nx * connectionData.videoWidth;
            const videoY = point.y * connectionData.videoHeight;

            const { offsetX, offsetY, displayWidth, displayHeight } =
                getCanvasResolutionAndOffset();

            const canvasX =
                (videoX * displayWidth) / connectionData.videoWidth + offsetX;
            const canvasY =
                (videoY * displayHeight) / connectionData.videoHeight + offsetY;
            return {
                x: canvasX,
                y: canvasY
            };
        },
        [connectionData, getCanvasResolutionAndOffset, isMirroredSelf]
    );

    const drawPath = useCallback(
        (path: CanvasPath) => {
            const context = contextRef.current!;
            context.strokeStyle = path.color;
            context.lineCap = "round";
            context.lineWidth = 5;

            for (const point of path.points) {
                const canvasPoint = videoPointToCanvas(point);

                context.lineTo(canvasPoint.x, canvasPoint.y);
                context.stroke();
                context.beginPath();
                context.moveTo(canvasPoint.x, canvasPoint.y);
            }
            context.beginPath();
        },
        [videoPointToCanvas]
    );

    const onCanvasPath = useCallback(
        (event: any) => {
            const { path } = event.detail;
            if (path.toConnectionId === connectionData.id) {
                drawPath(path);

                setPaths((prev) => ({ ...prev, [path.id]: path }));
            }
        },
        [connectionData.id, drawPath]
    );

    const clearAndRedraw = useCallback(() => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;

            canvas.width = canvas.clientWidth;
            canvas.height = canvas.clientHeight;

            for (const path of Object.values(paths)) {
                drawPath(path);
            }
        }
    }, [drawPath, paths]);

    const onPathErased = useCallback(
        (event: any) => {
            const { path } = event.detail;
            if (path.toConnectionId === connectionData.id) {
                setPaths((prev) => {
                    const updatedPaths = { ...prev };
                    delete updatedPaths[path.id];
                    return updatedPaths;
                });
                clearAndRedraw();
            }
        },
        [connectionData.id, clearAndRedraw]
    );

    const onSyncPing = useCallback(
        (event: any) => {
            const { id, timestamp } = event.detail;

            if (id !== connectionData.id) {
                return;
            }

            if (timestamp < lastTimestamp) {
                canvasDrawing.sendAllCanvasPaths(Object.values(paths), id);
            }
        },
        [connectionData.id, lastTimestamp, canvasDrawing, paths]
    );

    const onSync = useCallback(
        (event: any) => {
            const {
                paths,
                toConnectionId
            }: { paths: CanvasPath[]; toConnectionId: string } = event.detail;

            if (toConnectionId === connectionData.id) {
                setPaths(
                    paths.reduce(
                        (result, curr) => ({ ...result, [curr.id]: curr }),
                        {}
                    )
                );
                clearAndRedraw();
            }
        },
        [clearAndRedraw, connectionData.id]
    );

    useEffect(() => {
        const resizeObserver = new ResizeObserver(clearAndRedraw);
        resizeObserver.observe(connectionData.view);

        // Redraw when layout mode class changes on #video (contain ↔ cover)
        const videoEl = document.getElementById("video");
        const classObserver = videoEl
            ? new MutationObserver(clearAndRedraw)
            : null;
        classObserver?.observe(videoEl!, {
            attributes: true,
            attributeFilter: ["class"]
        });

        canvasDrawing.events.addEventListener("oncanvaspath", onCanvasPath);
        canvasDrawing.events.addEventListener("onpatherased", onPathErased);
        canvasDrawing.events.addEventListener("onsyncping", onSyncPing);
        canvasDrawing.events.addEventListener("onsync", onSync);

        return () => {
            canvasDrawing.events.removeEventListener(
                "oncanvaspath",
                onCanvasPath
            );
            canvasDrawing.events.removeEventListener(
                "onpatherased",
                onPathErased
            );
            canvasDrawing.events.removeEventListener("onsyncping", onSyncPing);
            canvasDrawing.events.removeEventListener("onsync", onSync);
            classObserver?.disconnect();

            resizeObserver.disconnect();
        };
    }, [
        canvasDrawing.events,
        connectionData.view,
        onCanvasPath,
        clearAndRedraw,
        onPathErased,
        onSyncPing,
        onSync
    ]);

    function normalizePointToVideo(point: Point): Point {
        const rect = canvasRef.current!.getBoundingClientRect();
        const clickX = point.x - rect.left;
        const clickY = point.y - rect.top;

        const { offsetX, offsetY, displayWidth, displayHeight } =
            getCanvasResolutionAndOffset();

        // Remove offset and scale to original video dimensions
        const videoX =
            (clickX - offsetX) * (connectionData.videoWidth / displayWidth);
        const videoY =
            (clickY - offsetY) * (connectionData.videoHeight / displayHeight);

        let nx = videoX / connectionData.videoWidth;
        const ny = videoY / connectionData.videoHeight;

        // Convert from mirrored screen space → true (unmirrored) video space
        if (isMirroredSelf) {
            nx = 1 - nx;
        }

        return { x: nx, y: ny };
    }

    function draw(point: Point) {
        if (!isDrawing) {
            return;
        }

        if (drawingMode === "eraser") {
            erase(point);
            return;
        }

        const videoPoint = normalizePointToVideo(point);
        setCurrentPoints((prev) => [...prev, videoPoint]);

        const context = contextRef.current!;
        const rect = canvasRef.current!.getBoundingClientRect();
        const x = point.x - rect.left;
        const y = point.y - rect.top;

        context.strokeStyle = strokeColor;
        context.lineCap = "round";
        context.lineWidth = 5;
        context.lineTo(x, y);
        context.stroke();
        context.beginPath();
        context.moveTo(x, y);
    }

    function erase(point: Point) {
        const videoPoint = normalizePointToVideo(point);

        const bboxHit = Object.values(paths).find((p) =>
            isPointInsideConvexHull(videoPoint, p.convexHull)
        );

        if (bboxHit && paths[bboxHit.id]) {
            canvasDrawing.eraseCanvasPath({
                id: bboxHit.id,
                toConnectionId: connectionData.id
            });

            setPaths((prev) => {
                const updatedPaths = { ...prev };
                delete updatedPaths[bboxHit.id];
                return updatedPaths;
            });
            clearAndRedraw();
        }
    }

    function startDrawing(point: Point) {
        if (drawingMode === "none") {
            return;
        }

        setIsDrawing(true);
        setCurrentPoints([]);

        if (drawingMode === "eraser") {
            erase(point);
        }
    }

    function stopDrawing() {
        if (!isDrawing) {
            return;
        }

        setIsDrawing(false);

        if (drawingMode === "eraser") {
            return;
        }

        // Reset the path
        contextRef.current!.beginPath();

        if (currentPoints.length > 0) {
            const path: CanvasPath = {
                id: v4(),
                points: currentPoints,
                color: strokeColor,
                toConnectionId: connectionData.id,
                convexHull: getConvexHull(currentPoints),
                timestamp: Date.now()
            };

            setPaths((prev) => ({ ...prev, [path.id]: path }));

            canvasDrawing.sendCanvasPath(path);
        }
    }

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: drawingMode !== "none" ? "auto" : "none"
            }}
            onMouseDown={(e) => startDrawing({ x: e.clientX, y: e.clientY })}
            onMouseMove={(e) => draw({ x: e.clientX, y: e.clientY })}
            onMouseUp={stopDrawing}
            onTouchStart={(e) =>
                startDrawing({
                    x: e.touches[0].clientX,
                    y: e.touches[0].clientY
                })
            }
            onTouchMove={(e) =>
                draw({ x: e.touches[0].clientX, y: e.touches[0].clientY })
            }
            onTouchEnd={stopDrawing}
        />
    );
}
